import { type PointerEvent, type ReactNode, useEffect, useRef } from 'react';

/** A press this long pauses instead of navigating (NEWS-05). */
export const HOLD_MS = 260;
/** Share of the width that goes back; the rest goes forward (NEWS-04). */
const BACK_ZONE = 1 / 3;

const INTERACTIVE = 'button, a, input, textarea, select, [role="button"]';

interface Press {
  side: 'prev' | 'next';
  /** Pending until the press counts as a hold. */
  timer: ReturnType<typeof setTimeout> | null;
  held: boolean;
}

/**
 * Stories gestures over the slide area (< 768px): a tap on the left third
 * goes back, on the right two thirds forward; pressing ≥ 260 ms holds the
 * player until the finger lifts or leaves. Presses on the slide's own
 * buttons are left to them. It listens on the area instead of covering it,
 * so the slide still scrolls inside (NEWS-14); when the browser takes the
 * touch for a scroll (`pointercancel`) nothing navigates.
 */
export function TapZones({
  enabled,
  onPrev,
  onNext,
  onHold,
  children,
}: {
  enabled: boolean;
  onPrev: () => void;
  onNext: () => void;
  onHold: (held: boolean) => void;
  children: ReactNode;
}) {
  const press = useRef<Press | null>(null);

  const clearPress = () => {
    const current = press.current;
    press.current = null;
    if (!current) return null;
    if (current.timer) clearTimeout(current.timer);
    return current;
  };

  useEffect(() => () => void clearPress(), []);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (!enabled || !event.isPrimary) return;
    if (event.target instanceof Element && event.target.closest(INTERACTIVE)) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const side =
      event.clientX - rect.left < rect.width * BACK_ZONE ? 'prev' : 'next';
    clearPress();
    const current: Press = { side, held: false, timer: null };
    current.timer = setTimeout(() => {
      current.timer = null;
      current.held = true;
      onHold(true);
    }, HOLD_MS);
    press.current = current;
  };

  /** Lifted: a short tap navigates, a hold just resumes. */
  const onPointerUp = () => {
    const current = clearPress();
    if (!current) return;
    if (current.held) onHold(false);
    else if (current.side === 'prev') onPrev();
    else onNext();
  };

  /** Left the area or became a scroll: never navigates. */
  const onPointerAbort = () => {
    const current = clearPress();
    if (current?.held) onHold(false);
  };

  return (
    <div
      data-testid="tap-zones"
      className="relative h-full"
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerAbort}
      onPointerCancel={onPointerAbort}
    >
      {children}
    </div>
  );
}
